import { CbhIcon } from "@clipboard-health/ui-components";
import { ModalStatus, useModalState } from "@clipboard-health/ui-react";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { useAppStateContext } from "@src/appV2/App/useAppState";
import { useGetClipboardScoreDelta } from "@src/appV2/AttendanceScore/api/useGetClipboardScoreDelta";
import { useHomeHealthVisibility } from "@src/appV2/ExperimentalHomeHealth/BookedCases/api/useHomeHealthVisibility";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { PRIORITY_ACCESS_COLOR } from "@src/appV2/lib/Theme/constants";
import { EnforceLocationPermissionsContainer } from "@src/appV2/LocationPermission/EnforceLocationPermissionsContainer";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { invalidateShifts } from "../../invalidateShifts";
import { MissedShifts } from "../../Shift/MissedShift/MissedShifts";
import { OpenShiftListDataProvider } from "../../Shift/Open/useOpenShiftListDataContext";
import { VirtualShiftListContextProvider } from "../../Shift/Open/useVirtualShiftListContext/useVirtualShiftListContext";
import { useScrollToTopOnStatusTap } from "../../useScrollToTopOnStatusTap";
import { ClearFiltersButton } from "../Filters/ClearFiltersButton";
import { ShiftDiscoverySearchModeSwitcher } from "../SearchModeSwitcher";
import { ClipboardCashProgressBottomSheet } from "./ClipboardCashProgressBottomSheet";
import { ListViewPageOpenShiftsList } from "./List";
import { ListViewPageHeader } from "./PageHeader";
import { PullToRefresh } from "./PullToRefresh";
// eslint-disable-next-line import/max-dependencies
import { useFilteredListViewOpenShiftsData } from "./useFilteredListViewOpenShiftsData";

function ClipboardScoreBanner({ onLearnMore }: { onLearnMore: () => void }) {
  const clipboardScoreReward = useCbhFlag(CbhFeatureFlag.CLIPBOARD_SCORE_REWARD, {
    defaultValue: {
      enabled: false,
    },
  });

  if (!clipboardScoreReward.enabled) {
    return null;
  }

  const currentDate = new Date();
  const endDate = new Date(clipboardScoreReward.endTime);

  if (currentDate >= endDate) {
    return null;
  }

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: PRIORITY_ACCESS_COLOR,
        color: "white",
        py: 2,
        px: 6,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <CbhIcon
          type="star-filled"
          size="small"
          sx={{
            color: "white",
            mr: 1,
          }}
        />
        <Box component="span" sx={{ fontWeight: 500, fontSize: "0.875rem" }}>
          Earn ${clipboardScoreReward.rewardAmountInDollars} cash with Clipboard Score
        </Box>
      </Box>
      <Box
        component="span"
        sx={{
          color: "white",
          textDecoration: "underline",
          cursor: "pointer",
          fontWeight: 600,
          fontSize: "0.875rem",
          padding: "4px 8px",
          "&:hover": {
            opacity: 0.8,
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            borderRadius: "4px",
          },
        }}
        onClick={onLearnMore}
      >
        Progress
      </Box>
    </Box>
  );
}

export function ShiftDiscoveryListViewPage() {
  const queryClient = useQueryClient();
  const scoreModalState = useModalState(ModalStatus.CLOSED);
  const clipboardScoreReward = useCbhFlag(CbhFeatureFlag.CLIPBOARD_SCORE_REWARD, {
    defaultValue: {
      enabled: false,
    },
  });

  const clipboardScoreDeltaQuery = useGetClipboardScoreDelta(
    clipboardScoreReward.enabled ? clipboardScoreReward.startTime : "",
    clipboardScoreReward.enabled ? clipboardScoreReward.endTime : "",
    { enabled: clipboardScoreReward.enabled }
  );

  const clipboardScoreDelta =
    clipboardScoreReward.enabled && clipboardScoreDeltaQuery.data
      ? clipboardScoreDeltaQuery.data.delta
      : 0;

  const {
    data: {
      filteredOpenShifts,
      unfilteredOpenShifts,
      workplacesMap,
      rateNegotiationsMap,
      priorityShiftsCount,
      urgentShiftsCount,
      blockShiftsCount,
      timeSlotFilters,
    },
    isLoading: openShiftsIsLoading,
    isSuccess: openShiftsIsSuccess,
    isError: openShiftsIsError,
    pagination,
    daysCount,
  } = useFilteredListViewOpenShiftsData();

  const { isHomeHealthEnabled, homeHealthCount } = useHomeHealthVisibility();

  const stackRef = useRef<HTMLDivElement>(null);

  useScrollToTopOnStatusTap(stackRef);

  const [shouldInfiniteScroll, setShouldInfiniteScroll] = useState<boolean>(false);

  useEffect(() => {
    const handleScroll = () => {
      // To prevent an infinite loop of infinite scrolling at idle, we should
      // allow infinite scrolling only when the user has scrolled to any amount
      if ((stackRef.current?.scrollTop ?? 0) > 0) {
        if (!shouldInfiniteScroll) {
          setShouldInfiniteScroll(true);
        }
      } else if (shouldInfiniteScroll) {
        setShouldInfiniteScroll(false);
      }
    };

    const element = stackRef.current;

    if (isDefined(element)) {
      element.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (isDefined(element)) {
        element.removeEventListener("scroll", handleScroll);
      }
    };
  }, [shouldInfiniteScroll]);

  const {
    state: { isAppActive },
  } = useAppStateContext();

  // This event is used for analytics for count of shoppers. The legacy event we were relying on was being sent on every app reopening, this is why we are adding the app state check.
  useLogEffect(APP_V2_USER_EVENTS.OPEN_SHIFTS_LIST_VIEWED, {}, { enabled: isAppActive });

  return (
    <Stack
      sx={(theme) => ({
        flex: 1,
        backgroundColor: theme.palette.background.tertiary,
        overflow: "hidden",
        position: "relative",
      })}
    >
      <EnforceLocationPermissionsContainer />
      <OpenShiftListDataProvider
        openShifts={filteredOpenShifts}
        workplacesMap={workplacesMap}
        rateNegotiationsMap={rateNegotiationsMap}
        isOpenShiftsLoading={openShiftsIsLoading}
        isOpenShiftsError={openShiftsIsError}
        isOpenShiftsSuccess={openShiftsIsSuccess}
        canLoadMoreShifts={Boolean(pagination && shouldInfiniteScroll && pagination?.hasNextPage)}
        isLoadingMoreShifts={pagination?.isFetchingNextPage ?? false}
        loadMoreShifts={() => {
          void pagination?.fetchNextPage();
        }}
      >
        <VirtualShiftListContextProvider
          timeSlotFilters={timeSlotFilters}
          shifts={filteredOpenShifts}
          workplacesMap={workplacesMap}
        >
          <ListViewPageHeader
            homeHealthEnabled={isHomeHealthEnabled}
            priorityShiftsCount={priorityShiftsCount}
            urgentShiftsCount={urgentShiftsCount}
            blockShiftsCount={blockShiftsCount}
            homeHealthCount={homeHealthCount}
          />

          <PullToRefresh
            scrollRef={stackRef}
            onRefresh={async () => {
              await invalidateShifts(queryClient);
            }}
          />
          <MissedShifts />

          <ClipboardScoreBanner
            onLearnMore={() => {
              scoreModalState.openModal();
            }}
          />
          <Stack
            ref={stackRef}
            data-testid="list-view-page-scroll-container"
            sx={{
              px: 5,
              overflow: "auto",
              flex: 1,
            }}
          >
            <ListViewPageOpenShiftsList daysCount={daysCount} scrollRef={stackRef} />
          </Stack>

          <ShiftDiscoverySearchModeSwitcher>
            <ClearFiltersButton allOpenShiftsCount={unfilteredOpenShifts.length} />
          </ShiftDiscoverySearchModeSwitcher>

          <ClipboardCashProgressBottomSheet
            modalState={scoreModalState}
            clipboardScoreDelta={clipboardScoreDelta}
          />
        </VirtualShiftListContextProvider>
      </OpenShiftListDataProvider>
    </Stack>
  );
}
